import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { toast } from 'react-toastify';
import API from '../api/axiosConfig';
import { requestPermissionAndToken, listenForegroundMessages } from '../firebase';

// Registra y configura push notifications nativas (solo en Android/iOS via Capacitor).
// En web sigue usando el FCM web SDK normal.
export async function registerNativePush(onToken, onNotification) {
  if (!Capacitor.isNativePlatform()) return;

  // Pedir permiso
  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return;

  // Registrar con FCM
  await PushNotifications.register();

  // Token FCM nativo → enviarlo al backend igual que el token web
  PushNotifications.addListener('registration', (token) => {
    if (onToken) onToken(token.value);
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[Push] Error de registro:', err);
  });

  // Notificación recibida con la app abierta
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    if (onNotification) onNotification(notification);
  });

  // Tap en notificación (app en background o cerrada) — el payload viene del
  // backend (push), pero por si algún día se acepta data arbitraria, nunca se
  // navega directo a esquemas tipo "javascript:" ni a cualquier origen externo
  // sin pasar por una pestaña nueva aislada (mismo patrón que LandingPopup.jsx).
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action.notification?.data?.url;
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (url.startsWith('/')) {
      window.location.href = url;
    }
  });
}

function isLoggedIn() {
  return !!(localStorage.getItem('accessToken') || localStorage.getItem('token'));
}

// Guarda el token FCM. Con sesión activa va a User.fcmToken (auth/fcm-token); sin
// sesión va a AnonymousDevice (notifications/device-token) — así igual se le puede
// mandar promociones/marketing a alguien que abrió la app pero nunca inició sesión.
function saveToken(token) {
  if (isLoggedIn()) {
    API.patch('/auth/fcm-token', { fcmToken: token }).catch(() => {});
  } else {
    API.post('/notifications/device-token', { fcmToken: token, platform: Capacitor.getPlatform() }).catch(() => {});
  }
}

// Punto único de registro de push usado por los notifiers (company/ryder) y por
// PushRegistrar (todos los usuarios, con o sin sesión): en la app nativa usa el SDK
// de Capacitor (el único que entrega notificaciones con la app en background o
// cerrada — el SDK web de Firebase no funciona embebido en el WebView, isSupported()
// da false ahí). En web sigue el flujo de siempre.
export async function initPushNotifications() {
  if (Capacitor.isNativePlatform()) {
    await registerNativePush(
      saveToken,
      (notification) => {
        const title = notification.title || notification.data?.title;
        const body  = notification.body  || notification.data?.body;
        if (title || body) toast.info(`${title}: ${body}`, { autoClose: 3000 });
      },
    );
    return;
  }

  const token = await requestPermissionAndToken();
  if (token) saveToken(token);
  listenForegroundMessages();
}
